import React, { useState } from "react";
import { Box, Button, Paper, Stack, Text } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useFormContext } from "react-hook-form";
import { useProfileStore } from "@/pages/ClientProfile/store/profileStore";
import { useParams } from "react-router-dom";
import { showNotification } from "@mantine/notifications";
import { showFieldError } from "@utils/reviewFieldUtils";
import { getDynamicFieldConfig } from "@/pages/ClientProfile/constants/dynamicFieldsConfig";
import ReviewItem from "./ReviewItem";
import type { ContentItem } from "@/pages/ClientProfile/types";

interface DynamicItemsFieldProps {
  item: ContentItem & {
    items?: any[];
    label: string;
    key: string;
  };
  formType: string | null;
  fieldKey: string;
  classes: Record<string, string>;
  refetchOCR?: () => Promise<void>;
}

const DynamicItemsField: React.FC<DynamicItemsFieldProps> = React.memo(
  ({ item, formType, fieldKey, refetchOCR, classes: _classes }) => {
    const { control: _control } = useFormContext();
    const { userId } = useParams();
    const addDynamicItem = useProfileStore((state) => state.addDynamicItem);
    const deleteDynamicItem = useProfileStore(
      (state) => state.deleteDynamicItem
    );
    const [addLoading, setAddLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState<number | null>(null);
    const config = getDynamicFieldConfig(formType, fieldKey);

    // If no config found and formType exists, try with uppercase form type as fallback
    const configWithFallback =
      config ||
      (formType
        ? getDynamicFieldConfig(formType.toUpperCase(), fieldKey)
        : null);

    if (!configWithFallback) {
      return null;
    }

    const currentItems = item.items || [];
    const isUserIdValid = Boolean(userId);

    const addNewItem = async () => {
      if (!configWithFallback) return;

      // Validate userId before proceeding
      if (!userId) {
        console.error(
          "[DynamicItemsField] Cannot add item: userId is undefined"
        );
        showNotification({
          title: "Error",
          message: "Unable to add item. Client ID is missing.",
          color: "red",
        });
        return;
      }

      setAddLoading(true);

      try {
        // Use simplified key format for add operation: forms.{form_id}.{field_key}
        // Extract form ID and field key from the base key
        // Note: Key format can be either "forms.{formId}.{fieldKey}" or "forms.{formId}.{extractionId}.{fieldKey}"
        const keyParts = item.key.split(".");
        const formId = keyParts[1];
        const fieldKey = keyParts[keyParts.length - 1]; // Always get the last part
        const simplifiedKey = `forms.${formId}.${fieldKey}`;

        // Make single API call to add new item - backend will create empty instance using Pydantic model
        await addDynamicItem(userId, simplifiedKey);

        showNotification({
          title: "Success",
          message: `Added new ${configWithFallback.itemLabel} item.`,
          color: "green",
        });
        setAddLoading(false);

        // The store method now handles OCR data updates automatically
        // Only do additional refetch if provided for specific use cases
        if (refetchOCR) await refetchOCR();
      } catch (error) {
        console.error("Error adding new item:", error);
        showFieldError(error, item.key);
        setAddLoading(false);
      }
    };

    const removeItem = async (itemIndex: number) => {
      // Validate userId before proceeding
      if (!userId) {
        console.error(
          "[DynamicItemsField] Cannot remove item: userId is undefined"
        );
        showNotification({
          title: "Error",
          message: "Unable to remove item. Client ID is missing.",
          color: "red",
        });
        return;
      }

      setDeleteLoading(itemIndex);
      try {
        // Generate the key for the item to remove using the current array index
        // Note: Key format can be either "forms.{formId}.{fieldKey}" or "forms.{formId}.{extractionId}.{fieldKey}"
        const keyParts = item.key.split(".");
        const formId = keyParts[1];
        const fieldKey = keyParts[keyParts.length - 1]; // Always get the last part
        const itemKey = `forms.${formId}.${fieldKey}.${itemIndex}`;

        await deleteDynamicItem(userId, itemKey);

        showNotification({
          title: "Success",
          message: `Removed ${configWithFallback.itemLabel} item.`,
          color: "green",
        });
        setDeleteLoading(null);

        // The store method now handles OCR data updates automatically
        // Only do additional refetch if provided for specific use cases
        if (refetchOCR) await refetchOCR();
      } catch (error) {
        console.error("Error removing item:", error);
        showFieldError(error, item.key);
        setDeleteLoading(null);
      }
    };

    return (
      <Stack gap="md">
        {/* Section Header */}
        <Text fw={500} size="sm" c="dimmed">
          {item.label}
        </Text>

        {/* Existing Items */}
        {currentItems.map((itemData, index) => (
          <Paper key={itemData.key || index} withBorder p="md" pos="relative">
            <Box pos="absolute" top={8} right={0}>
              <Button
                variant="subtle"
                color="red"
                size="xs"
                leftSection={<IconTrash size={14} />}
                onClick={() => removeItem(index)}
                disabled={
                  !isUserIdValid || deleteLoading === index || addLoading
                }
                loading={deleteLoading === index}
              >
                Remove
              </Button>
            </Box>

            <Text fw={500} size="sm" mb="md">
              {itemData.label || `${configWithFallback.itemLabel} ${index + 1}`}
            </Text>

            {/* Render fields for this item */}
            <Stack gap="sm">
              {Object.entries(itemData.fields || {}).map(
                ([_fieldName, fieldData]) => {
                  const fieldDataTyped = fieldData as any;
                  const fieldItem: ContentItem = {
                    type: "field",
                    key: fieldDataTyped.key,
                    label: fieldDataTyped.label,
                    value: fieldDataTyped.value,
                    reviewMode: fieldDataTyped.reviewMode,
                    indentLevel: 1,
                  };

                  return (
                    <Box key={fieldDataTyped.key + "-dynamic"} ml="md">
                      <ReviewItem item={fieldItem} />
                    </Box>
                  );
                }
              )}
            </Stack>
          </Paper>
        ))}

        {/* Add Button */}
        <Button
          variant="outline"
          leftSection={<IconPlus size={16} />}
          onClick={addNewItem}
          disabled={!isUserIdValid || addLoading || deleteLoading !== null}
          loading={addLoading}
          style={{
            borderColor: "#0077CC",
            color: "#0077CC",
          }}
        >
          Add {configWithFallback.itemLabel}
        </Button>
      </Stack>
    );
  }
);

DynamicItemsField.displayName = "DynamicItemsField";

export default DynamicItemsField;
